<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import {
  listLabelingDatasets,
  createLabelingDataset,
  triggerPartTraining,
} from '@/api/RobVisionApi';
import {
  type LabelingImage,
  type LabelingDataset,
  LabelingImageStatus,
  LabelingDatasetStatus,
} from '@/models/RobVision';
import { requiredRule } from '@/helpers/FormRules';
import formatDate from '@/helpers/DateFormatterHelper';
import TrainingModelChoiceDialog from '@/components/robvision/TrainingModelChoiceDialog.vue';
import { useAlertsStore } from '@/store/AlertsStore';
import gettext from '@/i18n/gettext';

const { $gettext, $ngettext } = gettext;
const alertsStore = useAlertsStore();
const loadingSpinnerStore = useLoadingSpinnerStore();

const props = defineProps<{
  projectUuid: string;
  // Used to prefill the model-name suggestion ("<project> v<n>").
  projectName?: string;
  images: LabelingImage[];
}>();

const isOpen = defineModel<boolean>('isOpen', { required: true });

const emit = defineEmits<{
  // A run was actually triggered — parents refresh the Models tab. Closing
  // the wizard without training must not fire it.
  'training-started': [];
}>();

const step = ref(1);
const filter = ref<'labeled' | 'all'>('labeled');
const selected = ref<Set<string>>(new Set());
const datasets = ref<LabelingDataset[]>([]);
const modelName = ref('');
const building = ref(false);
const buildError = ref('');
const builtVersion = ref<number | null>(null);
// True from "Start Model Training" until the trigger settles (or a step
// fails): the dataset build and the training trigger are one action for the
// user, covered by one loading state.
const launching = ref(false);
const startingTraining = ref(false);
const modelChoiceOpen = ref(false);
// Set = this launch trains the deprecated Mask R-CNN model with that
// rotation classifier step. Read when the trigger fires (possibly after
// the dataset build finished).
const deprecatedResolutionAngle = ref<number | undefined>(undefined);

// One spinner from the click until the dialog closes — the in-between
// states (build POST, polling, trigger) must never surface as a clickable
// pause.
watch(launching, (active) => {
  loadingSpinnerStore.setShowLoadingOverlay(active);
});

let pollTimer: ReturnType<typeof setTimeout> | null = null;

const readyImages = computed(() =>
  props.images.filter((image) => image.status === LabelingImageStatus.Ready),
);
const gridImages = computed(() =>
  filter.value === 'labeled' ? readyImages.value : props.images,
);
const selectedImages = computed(() =>
  props.images.filter((image) => selected.value.has(image.uuid)),
);
const totalInstances = computed(() =>
  selectedImages.value.reduce(
    (sum, image) => sum + (image.instanceCount ?? 0),
    0,
  ),
);
const selectedClasses = computed(() => [
  ...new Set(selectedImages.value.flatMap((image) => image.classes ?? [])),
]);
const nextVersion = computed(
  () => Math.max(0, ...datasets.value.map((dataset) => dataset.version)) + 1,
);
const notReadyCount = computed(
  () => props.images.length - readyImages.value.length,
);

watch(isOpen, async (open) => {
  if (open) {
    step.value = 1;
    filter.value = 'labeled';
    selected.value = new Set(readyImages.value.map((image) => image.uuid));
    building.value = false;
    buildError.value = '';
    builtVersion.value = null;
    launching.value = false;
    try {
      datasets.value = await listLabelingDatasets(props.projectUuid);
    } catch {
      datasets.value = [];
    }
    // Suggest "<project> — <date>" — mandatory, but editable (meeting
    // decision: every training run needs a name). Deliberately no version
    // number: the dataset version here and the resulting model version
    // differ, and the system stamps the real model version anyway.
    // With time — training twice a day is normal and the names must differ.
    modelName.value =
      `${props.projectName ?? ''} — ${formatDate(new Date().toISOString(), true)}`.trim();
  } else {
    stopPolling();
    // Closing mid-launch (e.g. via Esc on a failure state) must not leave
    // the app-wide overlay on.
    launching.value = false;
  }
});

onBeforeUnmount(stopPolling);

function stopPolling() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function toggle(image: LabelingImage) {
  // The backend rejects non-ready images at dataset creation; the "All"
  // view shows them for context but they cannot be selected.
  if (image.status !== LabelingImageStatus.Ready) {
    return;
  }
  // Vue instruments Set mutations on refs — no clone needed.
  if (selected.value.has(image.uuid)) {
    selected.value.delete(image.uuid);
  } else {
    selected.value.add(image.uuid);
  }
}

function selectAll() {
  selected.value = new Set(readyImages.value.map((image) => image.uuid));
}

async function createDataset() {
  building.value = true;
  buildError.value = '';
  try {
    const dataset = await createLabelingDataset(props.projectUuid, [
      ...selected.value,
    ]);
    builtVersion.value = dataset.version;
    pollDataset();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    building.value = false;
    buildError.value = error.message;
  }
}

async function pollDataset() {
  try {
    datasets.value = await listLabelingDatasets(props.projectUuid);
    // The dialog may have closed while the request was in flight — don't
    // reschedule past stopPolling.
    if (!isOpen.value) {
      return;
    }
    const dataset = datasets.value.find(
      (candidate) => candidate.version === builtVersion.value,
    );
    if (dataset?.status === LabelingDatasetStatus.Ready) {
      building.value = false;
      // Build and trigger are one user action — continue straight into the
      // training. The version-pinned trigger bypasses the "upload new data"
      // entitlement, so no pre-check is needed (or correct) here.
      if (launching.value) {
        await startTraining();
      }
      return;
    }
    if (dataset?.status === LabelingDatasetStatus.Failed) {
      building.value = false;
      launching.value = false;
      buildError.value = dataset.error ?? $gettext('The dataset build failed.');
      // Reopen Back/Start so the user can retry instead of a dead end.
      builtVersion.value = null;
      return;
    }
  } catch (error) {
    console.error('Dataset poll failed:', error);
  }
  // Never re-arm past stopPolling — a poll in flight while the dialog
  // closes would otherwise resume forever.
  if (!isOpen.value) {
    return;
  }
  pollTimer = setTimeout(pollDataset, 3000);
}

async function startTraining() {
  startingTraining.value = true;
  try {
    // Pin the run to the dataset built in this wizard — the slot alone could
    // have been overwritten by a concurrent build in the meantime.
    await triggerPartTraining(props.projectUuid, {
      datasetVersion: builtVersion.value ?? undefined,
      name: modelName.value.trim(),
      resolutionAngle: deprecatedResolutionAngle.value,
    });
    alertsStore.success($gettext('Training started.'));
    emit('training-started');
    isOpen.value = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    alertsStore.error(error.message);
  } finally {
    startingTraining.value = false;
    launching.value = false;
  }
}

// The single step-3 CTA: build the dataset, then start the training run.
// A dataset that already finished building (retry after a blocked trigger)
// is trained directly.
async function startModelTraining(resolutionAngle?: number) {
  if (launching.value || building.value || startingTraining.value) {
    return;
  }
  deprecatedResolutionAngle.value = resolutionAngle;
  launching.value = true;
  if (builtVersion.value !== null) {
    await startTraining();
    return;
  }
  await createDataset();
  if (buildError.value) {
    launching.value = false;
  }
}

const selectionText = computed(() =>
  $ngettext(
    '%{ n } image selected',
    '%{ n } images selected',
    selected.value.size,
    { n: String(selected.value.size) },
  ),
);
</script>

<template>
  <!-- The fixed height lives on the overlay content (see the unscoped style
       below): in a scrollable dialog Vuetify gives the card `flex-basis:
       100%`, which overrides any height set on the card itself. -->
  <v-dialog
    v-model="isOpen"
    max-width="960"
    scrollable
    content-class="train-wizard-overlay"
  >
    <v-card rounded="lg">
      <v-card-title class="d-flex align-center">
        {{ $gettext('Train New Model Version') }}
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :aria-label="$gettext('Close')"
          @click="isOpen = false"
        />
      </v-card-title>

      <!-- v-card-text is the scroll region of a scrollable dialog — without
           it the card grows with the step-1 grid and clips off-screen. -->
      <v-card-text class="pa-0">
        <v-stepper
          v-model="step"
          flat
          :items="[
            $gettext('Select Images'),
            $gettext('Set Name'),
            $gettext('Validate & Start Training'),
          ]"
          hide-actions
        >
          <!-- STEP 1: select images -->
          <template #item.1>
            <p class="text-body-2 text-medium-emphasis mb-3">
              {{
                $gettext(
                  'Pick the labeled images to include in this training run.',
                )
              }}
            </p>
            <div class="d-flex align-center mb-3">
              <v-btn-toggle
                v-model="filter"
                density="compact"
                mandatory
                rounded="pill"
              >
                <v-btn value="labeled" size="small">
                  {{ $gettext('Ready') }} {{ readyImages.length }}
                </v-btn>
                <v-btn value="all" size="small">
                  {{ $gettext('All') }} {{ images.length }}
                </v-btn>
              </v-btn-toggle>
              <v-spacer />
              <span class="text-caption text-medium-emphasis mr-3">
                {{ selectionText }}
              </span>
              <v-btn variant="text" size="small" @click="selectAll">
                {{ $gettext('Select All') }}
              </v-btn>
            </div>
            <v-row dense>
              <v-col
                v-for="image in gridImages"
                :key="image.uuid"
                cols="6"
                sm="4"
                md="3"
              >
                <v-card
                  rounded="lg"
                  :variant="selected.has(image.uuid) ? 'tonal' : 'outlined'"
                  :color="selected.has(image.uuid) ? 'primary' : undefined"
                  :disabled="image.status !== LabelingImageStatus.Ready"
                  class="image-tile"
                  :class="{ 'tile-selected': selected.has(image.uuid) }"
                  @click="toggle(image)"
                >
                  <v-img
                    :src="image.imageUrl"
                    aspect-ratio="1.4"
                    cover
                    class="bg-grey-darken-4"
                  >
                    <v-checkbox-btn
                      v-if="image.status === LabelingImageStatus.Ready"
                      :model-value="selected.has(image.uuid)"
                      color="primary"
                      class="tile-check"
                      density="compact"
                    />
                    <v-chip
                      v-if="image.status !== LabelingImageStatus.Ready"
                      size="x-small"
                      class="tile-status"
                      density="comfortable"
                    >
                      {{ $gettext('not ready') }}
                    </v-chip>
                  </v-img>
                  <v-card-text class="py-2 text-caption text-medium-emphasis">
                    {{
                      $ngettext(
                        '%{ n } object',
                        '%{ n } objects',
                        image.instanceCount ?? 0,
                        { n: String(image.instanceCount ?? 0) },
                      )
                    }}
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <!-- STEP 2: set name -->
          <template #item.2>
            <div class="wizard-narrow mx-auto">
              <p class="text-body-2 text-medium-emphasis mb-4">
                {{
                  $gettext(
                    'Name this training run. The selected labels are snapshotted as a new dataset version — earlier versions stay intact.',
                  )
                }}
              </p>
              <div class="text-caption text-medium-emphasis mb-1">
                {{ $gettext('Model name') }}
              </div>
              <v-text-field
                v-model="modelName"
                variant="outlined"
                density="comfortable"
                :rules="[requiredRule]"
                hide-details="auto"
                class="mb-4"
              />
              <div class="text-caption text-medium-emphasis mb-1">
                {{ $gettext('Dataset version') }}
              </div>
              <div
                class="version-preview d-flex align-center justify-space-between px-4 py-3"
              >
                <span class="text-body-2">
                  {{ projectName ?? $gettext('Dataset') }} ·
                  <strong>v{{ nextVersion }}</strong>
                </span>
                <v-chip size="x-small" color="success" density="comfortable">
                  {{ $gettext('new') }}
                </v-chip>
              </div>
            </div>
          </template>

          <!-- STEP 3: validate & start -->
          <template #item.3>
            <div class="wizard-narrow mx-auto">
              <p class="text-body-2 text-medium-emphasis mb-4">
                {{
                  $gettext('Review the dataset before starting model training.')
                }}
              </p>
              <v-card rounded="lg" variant="outlined" class="mb-4">
                <v-row no-gutters class="py-3">
                  <v-col cols="4" class="text-center stat-cell">
                    <div class="text-h4">{{ selected.size }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{ $ngettext('image', 'images', selected.size) }}
                    </div>
                  </v-col>
                  <v-col cols="4" class="text-center stat-cell">
                    <div class="text-h4">{{ totalInstances }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{ $ngettext('part', 'parts', totalInstances) }}
                    </div>
                  </v-col>
                  <v-col cols="4" class="text-center">
                    <div class="text-h4">{{ selectedClasses.length }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{
                        $ngettext('class', 'classes', selectedClasses.length)
                      }}
                    </div>
                  </v-col>
                </v-row>
              </v-card>
              <v-list density="compact" class="bg-transparent">
                <v-list-item
                  class="check-row mb-2"
                  prepend-icon="mdi-check-circle-outline"
                >
                  <v-list-item-title class="text-body-2 text-wrap">
                    {{
                      $ngettext(
                        '%{ n } labeled image selected',
                        '%{ n } labeled images selected',
                        selected.size,
                        { n: String(selected.size) },
                      )
                    }}
                  </v-list-item-title>
                </v-list-item>
                <!-- Guaranteed by the review flow: Mark as Labeled blocks
                     without a class, and pick-points are auto-generated. -->
                <v-list-item
                  class="check-row mb-2"
                  prepend-icon="mdi-check-circle-outline"
                >
                  <v-list-item-title class="text-body-2 text-wrap">
                    {{
                      $gettext('Every instance has a class and a pick-point')
                    }}
                  </v-list-item-title>
                </v-list-item>
                <!-- All-empty selections are legal (negative examples) but
                     rarely what the user meant — say it out loud. -->
                <v-list-item
                  v-if="selectedClasses.length === 0"
                  class="check-row check-row--warning mb-2"
                  prepend-icon="mdi-alert-circle-outline"
                >
                  <v-list-item-title class="text-body-2 text-wrap">
                    {{
                      $gettext(
                        'The selected images contain no objects — the dataset will only hold negative examples',
                      )
                    }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="notReadyCount > 0"
                  class="check-row check-row--warning mb-2"
                  prepend-icon="mdi-alert-circle-outline"
                >
                  <v-list-item-title class="text-body-2 text-wrap">
                    {{
                      $ngettext(
                        '%{ n } image is not labeled and will be excluded',
                        '%{ n } images are not labeled and will be excluded',
                        notReadyCount,
                        { n: String(notReadyCount) },
                      )
                    }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
              <p
                v-if="building"
                class="text-body-2 text-medium-emphasis mt-4 mb-0"
              >
                {{
                  $gettext('Building dataset v%{ v }…', {
                    v: String(builtVersion ?? nextVersion),
                  })
                }}
              </p>
              <v-alert
                v-if="buildError"
                type="error"
                variant="tonal"
                density="compact"
                class="mt-4"
              >
                {{ buildError }}
              </v-alert>
            </div>
          </template>
        </v-stepper>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <span
          v-if="step === 1 && notReadyCount > 0"
          class="text-caption text-medium-emphasis d-inline-flex align-center"
        >
          <v-icon icon="mdi-information-outline" size="small" class="mr-1" />
          {{
            $gettext(
              '%{ n } images are not marked ready and are excluded by default.',
              {
                n: String(notReadyCount),
              },
            )
          }}
        </span>
        <v-spacer />
        <r-btn
          v-if="step > 1 && !launching && !building && !startingTraining"
          variant="outlined"
          class="mr-2"
          @click="step--"
        >
          {{ $gettext('Back') }}
        </r-btn>
        <r-btn
          v-if="step < 3"
          variant="accent"
          icon="mdi-arrow-right"
          :disabled="selected.size === 0 || (step === 2 && !modelName.trim())"
          @click="step++"
        >
          {{ step === 2 ? $gettext('Next: Validate') : $gettext('Continue') }}
        </r-btn>
        <r-btn
          v-else
          variant="accent"
          icon="mdi-rocket-launch-outline"
          :disabled="
            selected.size === 0 ||
            !modelName.trim() ||
            launching ||
            building ||
            startingTraining
          "
          :loading="launching || building || startingTraining"
          @click="modelChoiceOpen = true"
        >
          {{ $gettext('Start Model Training') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <TrainingModelChoiceDialog
    v-model:isOpen="modelChoiceOpen"
    @current="startModelTraining()"
    @deprecated="startModelTraining"
  />
</template>

<style>
/* Unscoped: v-dialog teleports its content out of this component. A fixed
   height keeps the dialog from resizing between the steps (the card fills
   it via flex-basis 100%); the cap keeps it clear of the app header, which
   renders above the overlay. The heavy selector + !important are needed to
   beat VirtualRobotConfig's global `.v-dialog .v-overlay__content
   { max-height: 100% !important }` leak. */
.v-dialog > .v-overlay__content.train-wizard-overlay {
  height: 700px !important;
  max-height: calc(100vh - 160px) !important;
}
</style>

<style scoped>
/* Set-name / validate steps: narrow centered column like the design. */
.wizard-narrow {
  max-width: 480px;
}

.version-preview,
.check-row {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}
.check-row :deep(.v-list-item__prepend .v-icon) {
  color: rgb(var(--v-theme-success));
  opacity: 1;
}
.check-row--warning :deep(.v-list-item__prepend .v-icon) {
  color: rgb(var(--v-theme-warning));
}

.stat-cell {
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.image-tile {
  cursor: pointer;
}
.tile-check {
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgb(255, 255, 255);
  border-radius: 8px;
  /* Display-only: the tile's @click owns the selection. Without this, a
     click on the box toggles only the checkbox's internal state and drifts
     from the real selection. */
  pointer-events: none;
}
/* The tonal tint alone is invisible over bright photos — give selected
   tiles an unmistakable border. */
.tile-selected {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -1px;
}
.tile-status {
  position: absolute;
  top: 8px;
  right: 8px;
}
</style>
